import { jsx as _jsx } from "react/jsx-runtime";
import { Children, useCallback, useContext, useMemo, } from 'react';
import { useAppSelector } from 'shared/hooks/redux/hooks';
import { ReactSortable } from 'react-sortablejs';
import { HidingElement } from 'console/shared/components/features/hiding-element';
import { DeviceUISettingsContent } from 'console/shared/contexts/device-ui-settings/types';
import { select_ui_settings } from 'console/redux/settings-actions';
import { WindowContext } from 'console/shared/contexts/window/types';
import { DeviceUISettingsControl } from 'console/shared/contexts/device-ui-settings/control/types';
import { isMediumUI } from 'shared/app/lib/choose-ui-type';
import { GroupingElement } from '../grouping-element';
import { Box } from '@mui/material';
import { genericMemo } from 'console/shared/utils/memo';
const tab_padding = 32;
export const SortableCards = genericMemo(function SortableCardsImpl(props) {
    const { list, gap = 8, min_width = 165, min_height = 0, component, repeat_value = 'auto-fill', panel_key, className, ...rest } = props;
    const { size } = useContext(WindowContext);
    const panel_width = size.width - tab_padding;
    const { sorting_mode, grouping_mode } = useContext(DeviceUISettingsContent);
    const { grouping_panel_key, onAddCardToCustomPanel, onReorderCards, onVisibilityCardChange, } = useContext(DeviceUISettingsControl);
    const List = useMemo(() => list.map(component), [list, component]);
    const onHideElement = useCallback((index) => () => {
        onVisibilityCardChange(panel_key, list[index].id);
    }, [list, onVisibilityCardChange]);
    const onAddToCustomPanel = useCallback((index) => () => {
        if (grouping_panel_key) {
            onAddCardToCustomPanel(grouping_panel_key, +list[index].id);
        }
    }, [list, onAddCardToCustomPanel]);
    const { tab_cards_size_multiplier = 1 } = useAppSelector(select_ui_settings);
    const card_width = useMemo(() => {
        const current_width = min_width * tab_cards_size_multiplier;
        if (panel_width == 0) {
            return current_width;
        }
        return current_width > panel_width ? panel_width : current_width;
    }, [tab_cards_size_multiplier, min_width, panel_width]);
    const style = useMemo(() => {
        return {
            display: 'grid',
            width: '100%',
            gridTemplateColumns: `repeat(${repeat_value}, minmax(${isMediumUI() ? card_width * 1.3 : card_width}px, 1fr))`,
            gridTemplateRows: `repeat(${repeat_value}, minmax(${min_height}px, 1fr))`,
            gap,
            transition: 'all 0.3s ease-in-out',
            padding: '3px',
        };
    }, [min_height, card_width, gap, repeat_value]);
    const setNewList = useCallback((new_items, _, { dragging }) => {
        if (dragging == null) {
            return;
        }
        onReorderCards(panel_key, new_items);
    }, []);
    return (_jsx(ReactSortable, { list: list, setList: setNewList, animation: 200, disabled: !sorting_mode, style: style, delay: 80, scroll: true, scrollSpeed: 20, scrollSensitivity: 150, className: className ? `${className} sortable-card` : `sortable-card`, ...rest, children: Children.map(List, (child, index) => {
            if (grouping_mode) {
                return (_jsx(GroupingElement, { in_panel: false, addToPanel: onAddToCustomPanel(index), children: _jsx(Box, { height: "100%", children: child }) }, index));
            }
            const show = list[index].ui_settings.show;
            return (_jsx(HidingElement, { show: show, hide: onHideElement(index), children: _jsx(Box, { height: "100%", children: child }) }, index));
        }) }));
});
